import { ANIMATIONS } from '../utils/enums';

const TOKEN_KEY = 'MM_AUTH';

const AVATAR_FOLDER = 'https://managem8avatars.blob.core.windows.net/managem8avatars/';

const DEFAULT_CURRENCY = {
  id: 2,
  name: 'US Dollar',
  code: 'USD',
  mark: '$',
};

const DEFAULT_APPEARANCE = {
  theme: 'light',
  locale: 'en',
  dateFormat: 'DD/MM/YYYY',
};

const DEFAULT_LOCATION_ID = 1;

const SHOPIFY_AZURE_FUNCTION_URL = 'https://shopifyazurefunctions.azurewebsites.net';

const UUID_EXPIRATION_HOURS = 36;

const ANIMATION_PATHS = ['/dashboard', '/tasklists'];

const ANIMATION_PATH_MAP: { [key: string]: any } = {
  '/dashboard': ANIMATIONS.DASHBOARD,
  '/tasklists': ANIMATIONS.TASKLISTS,
};

type TPricingOption = {
  title: string
  subtitle: string
  price: string
  value: 'free' | 'lite' | 'standard' | 'enterprise'
  features: Array<{ item: string, children?: Array<string> }>
  isHighlighted?: boolean
};

const PRICING_OPTIONS: Array<TPricingOption> = [
  {
    title: 'personalFree',
    value: 'free',
    subtitle: 'perfectForIndividuals',
    price: '',
    features: [{
      item: 'freeUpTo',
    }, {
      item: 'allFeaturesDuring',
    },
    ],
    isHighlighted: false,
  },
  {
    title: 'Lite',
    value: 'lite',
    subtitle: 'perfectForSmall',
    price: '$1.99',
    features: [
      {
        item: 'teamManagement',
        children: [
          'addYourTeam',
          'integrateWithShopify',
        ],
      },
      {
        item: 'timesheets',
        children: [
          'trackYourEmployees',
        ],
      },
      {
        item: 'taskManager',
        children: [
          'automateYourShop',
          'assignTasks',
          'trackTheOverall',
        ],
      },
      {
        item: 'orderAttribution',
        children: [
          'integrateWithShopifyTo',
        ],
      },
      {
        item: 'commissions',
        children: [
          'integrateWithShopifyTo',
        ],
      },
      {
        item: 'scheduleManager',
        children: [
          'easilyPlan',
          'trackTimeOff',
          'seeADaily',
        ],
      },
    ],
    isHighlighted: false,
  },
  {
    title: 'standard',
    value: 'standard',
    subtitle: 'dayFree',
    price: '$3.99',
    features: [
      {
        item: 'analyticsDashboard',
        children: [
          'getAClear',
          'seeWhich',
          'learnWhich',
          'trackWorkforce',
        ],
      },
      {
        item: 'teamManagement',
        children: [
          'addYourTeam',
          'integrateWithShopify',
        ],
      },
      {
        item: 'timesheetsAndPerformance',
        children: [
          'trackYourEmployees',
        ],
      },
      {
        item: 'taskManager',
        children: [
          'automateYourShop',
          'assignTasks',
          'trackTheOverall',
        ],
      },
      {
        item: 'orderAttribution',
        children: [
          'integrateWithShopifyTo',
          'attributedSales',
        ],
      },
      {
        item: 'commissions',
        children: [
          'integrateWithShopifyTo',
          'attributedSales',
        ],
      },
      {
        item: 'scheduleManager',
        children: [
          'easilyPlan',
          'trackTimeOff',
          'seeADaily',
        ],
      },
      {
        item: 'payrollExports',
      },
      {
        item: 'integrations',
      },
    ],
    isHighlighted: true,
  },
  {
    title: 'custom',
    value: 'enterprise',
    subtitle: 'tailoredForLargeEnterprises',
    price: '',
    features: [
      {
        item: 'largeTeam',
      },
      {
        item: 'allFeatureIncluded',
      },
    ],
    isHighlighted: true,
  },
];

const FONT_PRIMARY = '"Public Sans Variable", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"';

const COLORS = [
  '#A855F7',
  '#FFAB00',
  '#00B8D9',
  '#FF5630',
  '#36B37E',
  '#B76E00',
  '#0A5554',
  '#006C9C',
  '#003768',
  '#aa2d52',
  '#F7D2FF',
  '#e089e9',
  '#5c671d',
  '#CCFAFF',
  '#a75a57',
  '#FFBD98',
  '#60F1C8',
  '#83CFFF',
  '#FF8F6D',
  '#8a8efa',
  '#868678',
  '#93ea7e',
  '#A4F7CC',
  '#562e5b',
  '#4a599f',
  '#212B36',
  '#581C87',
];

const PACKAGES_CONFIG: { [key: string]: {
  routes: Array<string>
  features: { [key: string]: boolean }
} } = {
  lite: {
    routes: [
      '/team',
      '/timesheets',
      '/schedules',
      '/tasklists',
      '/commissions',
      '/schedules/weekly',
      '/schedules/monthly',
      '/schedules/timeoff',
      '/orders',
      '/commissions/plans',
      '/commissions/sales',
      '/help',
    ],
    features: {
      analytics: false,
      reports: false,
      teamCommissions: false,
    },
  },
  standard: {
    routes: [
      '/dashboard',
      '/team',
      '/timesheets',
      '/schedules',
      '/payroll',
      '/tasklists',
      '/commissions',
      '/orders',
      '/commissions/teams',
      '/commissions/plans',
      '/commissions/sales',
      '/analytics/commission',
      '/analytics/dor',
      '/analytics',
      '/schedules/weekly',
      '/schedules/monthly',
      '/schedules/timeoff',
      '/inventory',
      '/help',
    ],
    features: {
      analytics: true,
      reports: true,
      teamCommissions: true,
    },
  },
  enterprise: {
    routes: [
      '/dashboard',
      '/team',
      '/timesheets',
      '/schedules',
      '/payroll',
      '/tasklists',
      '/commissions',
      '/orders',
      '/commissions/teams',
      '/analytics/commission',
      '/analytics/dor',
      '/analytics',
      '/commissions/plans',
      '/commissions/sales',
      '/schedules/weekly',
      '/schedules/monthly',
      '/schedules/timeoff',
      '/inventory',
      '/help',
    ],
    features: {
      analytics: true,
      reports: true,
      teamCommissions: true,
    },
  },
  free: {
    routes: [
      '/dashboard',
      '/team',
      '/timesheets',
      '/schedules',
      '/payroll',
      '/tasklists',
      '/commissions',
      '/orders',
      '/commissions/teams',
      '/analytics/commission',
      '/analytics/dor',
      '/analytics',
      '/commissions/plans',
      '/commissions/sales',
      '/schedules/weekly',
      '/schedules/monthly',
      '/schedules/timeoff',
      '/help',
    ],
    features: {
      analytics: true,
      reports: true,
      teamCommissions: true,
    },
  },
};

const HEADER = {
  H_MOBILE: 64,
  H_MAIN_DESKTOP: 88,
  H_DASHBOARD_DESKTOP: 92,
  H_DASHBOARD_DESKTOP_OFFSET: 88 - 32,
};

const NAV = {
  W_BASE: 260,
  W_DASHBOARD: 280,
  W_DASHBOARD_MINI: 88,
  //
  H_DASHBOARD_ITEM: 48,
  H_DASHBOARD_ITEM_SUB: 36,
  //
  H_DASHBOARD_ITEM_HORIZONTAL: 32,
};

const NAV_ICON = {
  NAV_ITEM: 24,
  NAV_ITEM_HORIZONTAL: 22,
  NAV_ITEM_MINI: 22,
};

const ROUTES = {
  COMMISSION_PLANS: '/commissions/plans',
  COMMISSION_TEAMS: '/commissions/teams',
  WORK_SCHEDULES: '/schedules/weekly',
  CREATE_COMMISSIONS: '/commissions/create',
  COMMISSION_SALES: '/commissions/sales',
  COMMISSION_MODELS: '/commissions/models',
  SCHEDULES_BULK: '/schedules/bulk',
  TEAM_BULK: '/team/bulk',
  SETTINGS: '/settings',
  TEAM: '/team',
  INVENTORY: '/inventory',
  HELP: '/help',
  CALENDAR: '/schedules/monthly',
  ANALYTICS: {
    COMMISSION_INDIVIDUAL: '/analytics/commission/individual',
    COMMISSION_TEAMS: '/analytics/commission/teams',
    ROOT: '/analytics',
    DOR: '/analytics/dor',
  },
};

const INDEX_TO_COLOR: { [key: number]: string } = {
  0: 'success',
  1: 'warning',
  2: 'error',
  3: 'default',
  4: 'primary',
  5: 'secondary',
  6: 'success',
  7: 'warning',
  8: 'error',
  9: 'default',
  10: 'primary',
  11: 'secondary',
  12: 'success',
  13: 'warning',
  14: 'error',
  15: 'default',
  16: 'primary',
  17: 'secondary',
};

const BASE_LOCATION = [{
  id: 1,
  name: 'Back Office',
}];

const SYSTEM_FORMATS = ['DD/MM/YYYY', 'MM/DD/YYYY'];

export {
  TOKEN_KEY,
  AVATAR_FOLDER,
  DEFAULT_CURRENCY,
  DEFAULT_APPEARANCE,
  DEFAULT_LOCATION_ID,
  SHOPIFY_AZURE_FUNCTION_URL,
  UUID_EXPIRATION_HOURS,
  ANIMATION_PATHS,
  ANIMATION_PATH_MAP,
  PRICING_OPTIONS,
  COLORS,
  FONT_PRIMARY,
  PACKAGES_CONFIG,
  NAV,
  NAV_ICON,
  HEADER,
  ROUTES,
  INDEX_TO_COLOR,
  BASE_LOCATION,
  SYSTEM_FORMATS,
};
